import React, { useEffect, useState } from 'react';
import ProcessButton from '../../ProcessButton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getUserDetails, getUserOrgsString } from '../../../store/user/userSlice';
import {
	callAPISync,
	createUploadHeader,
	getKeyphraseFiles,
	logProcessingStarted,
	publishMessage,
	sendSubscribeMessage,
} from '../../../utils/api';
import Logger from '../../../utils/logger';
import { getUploadData, resetFileUpload } from '../../../store/upload/uploadSlice';
import { beginProcessing } from '../../../store/processing/processingSlice';
import { Processors } from '../../../types/generic';
import { SubscriptionOptions } from '../../../utils/processorTopics';
import { CellebriteXMLMessage, KeyphrasesInfo } from '../../../types/upload';
import { KeyphraseSelect } from '../../KeyphraseSelect';
import { useLogto } from '@logto/react';
import { UploadFormStep } from '../../Tabs/Upload/UploadTabSkeleton/stepWrapper';
import { useUploadFormStepper } from '../../Tabs/Upload/UploadFormContext';
import EnrichmentSelect from '../../EnrichmentSelect';
import { ENRICHMENT_OPTIONS } from '../../../utils/enrich';

const CellebriteXMLMetadata: React.FC<{ index?: number }> = ({ index: baseIndex = 0 }) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const userDetails = useAppSelector((state) => getUserDetails(state));
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const userTeams = useAppSelector((state) => getUserOrgsString(state));
	const notifyOnComplete = useAppSelector((state) => state.processing.notifyOnComplete);
	const userRoles = useAppSelector((state) => state.tab.userRoles);

	const {
		fileName,
		fileOperation,
		deviceOwner,
		deviceOwnerType,
		suspectKnown,
		fileId,
		uploadReason,
		duplicatePreference,
		useCase,
		enrichments,
	} = useAppSelector((state) => getUploadData(state));

	const [keyphraseOptions, setKeyphraseOptions] = useState<KeyphrasesInfo[]>([]);
	const [selectedKeyphrase, setSelectedKeyphrase] = useState<KeyphrasesInfo>();
	const [keyphraseError, setKeyphraseError] = useState<string>('');
	const [loadingKeyphrases, setLoadingKeyphrases] = useState<boolean>(true);
	const [publishError, setPublishError] = useState<string>('');

	const allEnrichments = Object.entries(ENRICHMENT_OPTIONS);
	const availableEnrichments = allEnrichments.filter((item) => userRoles.includes(item[1].reqRole));

	const onSubmit = () => {
		if (selectedKeyphrase) {
			const cellebriteXMLMessage: CellebriteXMLMessage = {
				...createUploadHeader(deviceOwner, fileName, userDetails, fileOperation, uploadReason, forceDetails, useCase, deviceOwnerType),
				metadata: {
					keywordsInfo: selectedKeyphrase,
					enrichments: enrichments,
				},
			};
			callAPISync(logto, (token) => {
				logProcessingStarted(
					token,
					fileOperation,
					fileName,
					uploadReason,
					fileId,
					userDetails.name,
					userTeams,
					userDetails.email,
					suspectKnown,
					selectedKeyphrase.location,
					selectedKeyphrase.lastModified,
					notifyOnComplete
				);
				sendSubscribeMessage(SubscriptionOptions.CELLEBRITE_XML, token);
				publishMessage(cellebriteXMLMessage, 'upload.cellebrite.xml', fileId, token, duplicatePreference)
					.then(async (res) => {
						const txt = await res.text();
						if (res.ok) {
							Logger.debug('Submitted:\n', txt);
							dispatch(
								beginProcessing({
									fileName,
									fileId,
									operation: fileOperation,
									processor: Processors.CELLEBRITE_XML,
									duplicatePreference,
									enrichments,
								})
							);
							dispatch(resetFileUpload());
						} else {
							Logger.error('Error submitting');
							Logger.error(res.status, res.statusText);
							setPublishError(res.statusText);
						}
					})
					.catch((e) => {
						Logger.error(e);
						setPublishError(JSON.stringify(e));
					});
			});
		} else {
			setKeyphraseError('A keyphrases file must be selected to begin processing. ');
		}
	};

	useEffect(() => {
		callAPISync(logto, (token) => {
			setLoadingKeyphrases(true);

			getKeyphraseFiles(token, 'cellebrite-xml')
				.then((res) => {
					if (res.ok) {
						return res.json();
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: KeyphrasesInfo[]) => {
					setKeyphraseOptions(resp);
					setLoadingKeyphrases(false);
				})
				.catch((e) => {
					setKeyphraseError(`An error occurred when fetching the keyphrases list (${String(e)})! `);
					setLoadingKeyphrases(false);
					Logger.error(e);
				});
		});
	}, []);

	const enrichStep = (
		<UploadFormStep
			key="cellebriteXMLEnrich"
			label="Select Enrichments (optional)"
			valid={true}
			validLabel={`Enrichments Selected (${enrichments && enrichments.length > 0 ? enrichments : 'none'})`}
		>
			<EnrichmentSelect />
		</UploadFormStep>
	);

	const steps = [
		<UploadFormStep
			key="keyphrasesCellebriteXML"
			label="Select a Keyphrase List"
			valid={Boolean(selectedKeyphrase)}
			validLabel={`Keyphrase List Selected (${selectedKeyphrase?.title})`}
			errorMessage="A keyphrase list must be selected"
		>
			<KeyphraseSelect
				selectedList={selectedKeyphrase}
				setSelectedList={(e) => setSelectedKeyphrase(e)}
				errorText={keyphraseError}
				keyphraseOptions={keyphraseOptions}
				helperText={'Choose the keyphrases list to be used when processing the file:'}
				isLoading={loadingKeyphrases}
			/>
		</UploadFormStep>,
		<UploadFormStep key="cellebriteProcess" label="Final Confirmation" valid={true} validLabel="">
			<ProcessButton
				operation={fileOperation}
				onClick={onSubmit}
				pipelineName="Cellebrite"
				publishError={publishError}
				setPublishError={setPublishError}
			/>
		</UploadFormStep>,
	];

	if (availableEnrichments.length > 0) {
		steps.splice(1, 0, enrichStep);
	}

	const { activeStep } = useUploadFormStepper();

	return (
		<>
			{steps.map((step, i) => {
				const stepIndex = baseIndex + i;

				return React.cloneElement(step, {
					index: stepIndex,
					active: activeStep === stepIndex,
					completed: activeStep > stepIndex,
				});
			})}
		</>
	);
};

export default CellebriteXMLMetadata;
